// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getDateFull from "@store/Framework/Common/Utilities/getDateFull";
import getDateWithoutDay from "@store/Framework/Common/Utilities/getDateWithoutDay";
import validateIsDateDifferentDay from "@store/Framework/Common/Utilities/validateIsDateDifferentDay";
import EventDetailsAddToCalendar from "@store/Library/Components/Events/EventDetailsAddToCalendar";
import Clock from "@store/Library/Components/Icons/Clock";
import { AddressModel, CanadianTimeZones } from "@store/Library/Pages/EventDetailsPage";
import useMergeProps from "@toolkit/Framework/Common/Hooks/useMergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React from "react";
import { css } from "styled-components";

interface Props {
    startDate: Date;
    endDate?: Date;
    hideHourMinutes?: boolean;
    dateTimezone?: CanadianTimeZones;
    title?: string;
    eventSummary?: string;
    isLocalBranch?: boolean;
    addressWrapper?: AddressModel[];
    showTitle?: boolean;
    lightText?: boolean;
    showAddToCal?: boolean;
    extendedStyles?: EventDateStyles;
}

export interface EventDateStyles {
    dateWrapper?: InjectableCss;
    dateIcon?: IconPresentationProps;
    lightTextWrapper?: InjectableCss;
    titleDate?: TypographyPresentationProps;
    textDate?: TypographyPresentationProps;
    timezoneLabel?: TypographyPresentationProps;
}

export const eventDateStyles: EventDateStyles = {
    dateWrapper: {
        css: css`
            display: flex;
            align-items: flex-start;
            flex-wrap: nowrap;
        `,
    },
    dateIcon: {
        css: css`
            margin-right: ${pxToRem(10)};
        `,
    },
    lightTextWrapper: {
        css: css`
            color: white;
        `,
    },
    titleDate: {
        css: css`
            display: inline-block;
            font-weight: ${x => x.theme.design.typography.weight.semibold};
        `,
    },
    textDate: {
        css: css`
            display: block;
            font-weight: ${x => x.theme.design.typography.weight.base};
        `,
    },
    timezoneLabel: {
        css: css`
            margin-left: ${pxToRem(5)};
            padding: ${pxToRem(0, 4)};
            border-radius: ${pxToRem(2)};
            background-color: ${getColor("info.light")};
            color: ${getColor("text")};
            font-size: ${pxToRem(10)};
            font-weight: ${x => x.theme.design.typography.weight.semibold};
            line-height: ${x => x.theme.design.typography.lineHeight.semi};
            white-space: nowrap;
            vertical-align: middle;
        `,
    },
};

const EventDate = ({
    startDate,
    endDate,
    hideHourMinutes,
    dateTimezone,
    title,
    eventSummary,
    isLocalBranch,
    addressWrapper,
    showTitle,
    lightText,
    showAddToCal,
    extendedStyles,
}: Props) => {
    const styles = useMergeProps(eventDateStyles, extendedStyles);

    let currentTimezoneName;
    let currentTimezoneValue;
    switch (dateTimezone) {
        case "MT":
            currentTimezoneName = translate("Mountain Time");
            currentTimezoneValue = translate("America/Edmonton");
            break;
        case "CT":
            currentTimezoneName = translate("Central Time");
            currentTimezoneValue = translate("America/Winnipeg");
            break;
        case "ET":
            currentTimezoneName = translate("Eastern Time");
            currentTimezoneValue = translate("America/Toronto");
            break;
        case "AT":
            currentTimezoneName = translate("Atlantic Time");
            currentTimezoneValue = translate("America/Halifax");
            break;
        case "Local":
            currentTimezoneName = translate("Local Time");
            currentTimezoneValue = Intl.DateTimeFormat().resolvedOptions().timeZone;
            break;
        default:
            currentTimezoneName = translate("Pacific Time");
            currentTimezoneValue = translate("America/Vancouver");
            break;
    }

    // eslint-disable-next-line spire/avoid-dynamic-translate
    const dateSeparator = ` ${translate("to")} `;

    const getEndDate = () => {
        if (!endDate || (hideHourMinutes && !validateIsDateDifferentDay(startDate, endDate))) {
            return null;
        }

        if (validateIsDateDifferentDay(startDate, endDate)) {
            return dateSeparator + getDateFull(endDate, hideHourMinutes);
        }

        return dateSeparator + getDateWithoutDay(endDate);
    };

    return (
        <StyledWrapper {...styles.dateWrapper}>
            <Icon src={Clock} {...styles.dateIcon} color={lightText ? "white" : "primary"} />
            <StyledWrapper {...(lightText && styles.lightTextWrapper)}>
                {showTitle && <Typography {...styles.titleDate}>{translate("Event Date")}</Typography>}
                <Typography {...(showTitle ? styles.textDate : styles.titleDate)}>
                    {getDateFull(startDate, hideHourMinutes)}
                    {endDate && getEndDate()}
                    {!hideHourMinutes && <Typography {...styles.timezoneLabel}>{currentTimezoneName}</Typography>}
                </Typography>
                {showAddToCal && title && startDate && (
                    <EventDetailsAddToCalendar
                        eventTitle={title}
                        startDate={startDate}
                        eventSummary={eventSummary}
                        endDate={endDate}
                        isAllDay={hideHourMinutes}
                        dateTimezone={currentTimezoneValue}
                        isLocalBranch={isLocalBranch}
                        addressWrapper={addressWrapper}
                    />
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default EventDate;
